<template>
  <section class="trips-report">
    <div class="w-full main-card px-3 md:px-4 lg:px-5 py-4 lg:py-5 xl:py-7">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 class="!mb-1">Trips Report</h2>
          <p class="text-text-light !mb-0">
            Pick an employer (community) and download CSV exports of their member trip data and
            the points policies that were active during the selected range.
          </p>
        </div>
      </div>

      <div class="mt-5">
        <div class="block text-sm font-medium mb-1">Employer</div>
        <EmployerSelect v-model:value="employerId" />
      </div>

      <div class="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div class="report-card">
          <div class="flex items-start gap-3">
            <div class="report-card__icon">
              <PhMapTrifold :size="22" weight="duotone" />
            </div>
            <div class="flex-1">
              <h3 class="!mb-1">Trip Data</h3>
              <p class="text-text-light !mb-3">
                One row per member with their initial declared commute modes (Mon–Sun) and one
                column per date in the range listing the logged trip modes for that day.
              </p>
            </div>
          </div>

          <div class="mt-2 flex flex-col gap-3">
            <div>
              <div class="block text-sm font-medium mb-1">Date range</div>
              <RangePicker
                v-model:value="tripDates"
                class="w-full"
                format="YYYY-MM-DD"
                :allow-clear="true"
                :disabled-date="disableFutureDate"
              />
              <p class="text-xs text-text-light mt-1">Maximum range: 731 days.</p>
            </div>

            <Checkbox v-model:checked="workTripsOnly">Work trips only</Checkbox>

            <Button
              type="primary"
              class="btn-gradient self-start"
              size="large"
              :loading="tripLoading"
              :disabled="!canDownloadTripData"
              @click="downloadTripData"
            >
              <template #icon>
                <PhDownloadSimple :size="18" weight="bold" />
              </template>
              <span class="ml-2">Download Trip Data CSV</span>
            </Button>
          </div>
        </div>

        <div class="report-card">
          <div class="flex items-start gap-3">
            <div class="report-card__icon">
              <PhCoins :size="22" weight="duotone" />
            </div>
            <div class="flex-1">
              <h3 class="!mb-1">Points Policy</h3>
              <p class="text-text-light !mb-3">
                One row per points policy version whose validity window overlaps the range —
                including the platform default and superseded versions — with the points per
                transportation mode.
              </p>
            </div>
          </div>

          <div class="mt-2 flex flex-col gap-3">
            <div>
              <div class="block text-sm font-medium mb-1">Date range</div>
              <RangePicker
                v-model:value="policyDates"
                class="w-full"
                format="YYYY-MM-DD"
                :allow-clear="true"
                :disabled-date="disableFutureDate"
              />
            </div>

            <Button
              type="primary"
              class="btn-gradient self-start"
              size="large"
              :loading="policyLoading"
              :disabled="!canDownloadPolicy"
              @click="downloadPointsPolicy"
            >
              <template #icon>
                <PhDownloadSimple :size="18" weight="bold" />
              </template>
              <span class="ml-2">Download Points Policy CSV</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { Button, Checkbox, DatePicker, message } from 'ant-design-vue';
import { PhMapTrifold, PhCoins, PhDownloadSimple } from 'phosphor-vue';
import dayjs from 'dayjs';
import dataExportsApi from '@/services/apis/admin/dataExports.api';
import handleResErrors from '@/utils/handleResErrors';
import EmployerSelect from '@/components/admin/PlatformStats/EmployerSelect.vue';

export default {
  components: {
    Button,
    Checkbox,
    EmployerSelect,
    RangePicker: DatePicker.RangePicker,
    PhMapTrifold,
    PhCoins,
    PhDownloadSimple,
  },
  data() {
    const end = dayjs();
    const start = end.subtract(29, 'day');
    return {
      tripDates: [start, end],
      policyDates: [start, end],
      workTripsOnly: false,
      tripLoading: false,
      policyLoading: false,
      employerId: undefined,
    };
  },
  computed: {
    tripRangeValid() {
      return this.isValidRange(this.tripDates);
    },
    policyRangeValid() {
      return this.isValidRange(this.policyDates);
    },
    canDownloadTripData() {
      return Boolean(this.employerId) && this.tripRangeValid;
    },
    canDownloadPolicy() {
      return Boolean(this.employerId) && this.policyRangeValid;
    },
  },
  methods: {
    isValidRange(range) {
      return Array.isArray(range) && range.length === 2 && range[0] && range[1];
    },
    disableFutureDate(current) {
      return current && current > dayjs().endOf('day');
    },
    formatRange(range) {
      return {
        startDate: dayjs(range[0]).format('YYYY-MM-DD'),
        endDate: dayjs(range[1]).format('YYYY-MM-DD'),
      };
    },
    async downloadTripData() {
      if (!this.canDownloadTripData) return;
      this.tripLoading = true;
      try {
        await dataExportsApi.downloadTripDataCsv({
          employerId: this.employerId,
          ...this.formatRange(this.tripDates),
          workTripsOnly: this.workTripsOnly,
        });
      } catch (error) {
        message.error(handleResErrors(error));
      }
      this.tripLoading = false;
    },
    async downloadPointsPolicy() {
      if (!this.canDownloadPolicy) return;
      this.policyLoading = true;
      try {
        await dataExportsApi.downloadPointsPolicyCsv({
          employerId: this.employerId,
          ...this.formatRange(this.policyDates),
        });
      } catch (error) {
        message.error(handleResErrors(error));
      }
      this.policyLoading = false;
    },
  },
};
</script>

<style scoped>
.report-card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem;
  border: 1px solid var(--color-bg-dark);
  border-radius: 12px;
  background: var(--color-bg-light);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.report-card:hover {
  border-color: var(--color-primary-light);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.report-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--color-primary-outline);
  color: var(--color-primary);
  flex-shrink: 0;
}
</style>
